/**
 * Retrieve the given asset
 *
 * @param {String} assetPath
 * @returns String
 */
export const asset = (assetPath) => {
  return `${window.realIdAssetsUrl}/${assetPath}`;
};

/**
 *  Render a template content string into human readable format
 *
 * NOTE: yes it's called `selectedOrder` but in fact it's the order ID (#1111).
 *   It's a pullover from the new.js page. I'm just tired at the moment.
 *
 * @param {String} content
 * @param {Object} params
 * @return {String}
 */
export const renderContent = (content = "", params, forEmail = false) => {
  let renderedContent = content
    .replace(/\[firstName\]/g, params.firstName || "")
    .replace(/\[lastName\]/g, params.lastName || "");

  // not doing this leaves a weird 'undefined' string
  if (params.selectedOrder) {
    renderedContent = renderedContent.replace(
      /\[orderId\]/g,
      params.selectedOrder
    );
  } else {
    renderedContent = renderedContent.replace(/\[orderId\]/g, "");
  }
  if (forEmail) {
    renderedContent = renderedContent.replace(/\n+/g, "<br/>");
  }

  return renderedContent.replace("undefined", "");
};

/**
 * Generate a smart subject line that matches server-side logic from deliverChecks.ts
 * This ensures consistency between frontend preview and actual email delivery
 *
 * @param {Object} params
 * @param {String} params.customSubject - Custom subject provided by user
 * @param {String|Number} params.orderId - Order ID if available
 * @param {String} params.shopName - Shop name for personalization
 * @param {String} params.source - Source of the check (e.g., 'customer_registration')
 * @returns {String} Generated subject line
 */
export const generateSmartSubjectLine = ({
  customSubject = "",
  orderId = null,
  shopName = null,
  source = ""
} = {}) => {
  // If user provided a custom subject, use that with template rendering
  if (customSubject) {
    return customSubject;
  }

  // Default subject line logic matching server-side deliverChecks.ts
  if (orderId) {
    // Subject with order ID: matches server-side subjectWithOrder template
    if (shopName) {
      return `Your recent ${shopName} order #${orderId} requires ID verification`;
    } else {
      return `Your recent order #${orderId} requires ID verification`;
    }
  } else if (source === "customer_registration") {
    // Customer registration subject: matches server-side subjectCustomerRegistration template
    if (shopName) {
      return `Your ${shopName} account requires ID verification`;
    } else {
      return `Your account requires ID verification`;
    }
  } else {
    // Subject without order: matches server-side subjectWithoutOrder template
    if (shopName) {
      return `Your recent ${shopName} order requires ID verification`;
    } else {
      return `Your recent order requires ID verification`;
    }
  }
};

/**
 * Generate subject line for preview that handles both custom and smart defaults
 * This function combines custom subject rendering with smart fallbacks
 *
 * @param {Object} formValues - Current form values from Formik
 * @param {String} shopName - Shop name for fallback generation
 * @returns {String} Rendered subject line for preview
 */
export const generateSubjectPreview = (formValues, shopName) => {
  const baseSubject = generateSmartSubjectLine({
    customSubject: formValues.customSubject,
    orderId: formValues.wcOrderId,
    shopName: shopName,
    source: formValues.source || ""
  });

  // If we have a custom subject or template with shortcodes, render it
  if (formValues.customSubject || baseSubject.includes('[')) {
    return renderContent(baseSubject, {
      firstName: formValues.firstName || "[firstName]",
      lastName: formValues.lastName || "[lastName]",
      selectedOrder: formValues.wcOrderId ? `#${formValues.wcOrderId}` : "[orderId]"
    });
  }

  // Return the smart default as-is
  return baseSubject;
};
